import React from "react";
import Navbar from "../components/navbar";
import Hero from "../components/hero";
import Footer from "../components/footer";
import "../styles/universal.css";
import "../styles/privacyPage.css";

const PrivacyPage = () => {
  return (
    <div className="privacy-page">
      <Navbar />
      <Hero
        title="Privacy Policy"
        subtitle="Your privacy and confidentiality are of utmost importance to us."
      />

      <div className="privacy-container">
        <div className="privacy-content">
          <p className="last-updated">Last Updated: January 2026</p>

          <section className="privacy-section">
            <h2>Introduction</h2>
            <p>
              Erin Robinson Counselling is committed to protecting your privacy
              and maintaining the confidentiality of your personal and health
              information. This Privacy Policy outlines how we collect, use,
              store, and protect your information in accordance with the
              Protection of Personal Information Act (POPIA), Act 4 of 2013, and
              the ethical guidelines of the Health Professions Council of South
              Africa (HPCSA).
            </p>
          </section>

          <section className="privacy-section">
            <h2>Information We Collect</h2>
            <p>We may collect the following types of information:</p>
            <ul>
              <li>
                <strong>Personal Information:</strong> Name, contact details
                (phone number, email address, physical address), date of birth,
                and identity number.
              </li>
              <li>
                <strong>Health Information:</strong> Information related to your
                mental health, counselling sessions, assessment results,
                treatment plans, and any other relevant clinical information.
              </li>
              <li>
                <strong>Financial Information:</strong> Medical aid details,
                billing information, and payment records.
              </li>
              <li>
                <strong>Session Records:</strong> Notes from counselling
                sessions, correspondence, and reports.
              </li>
            </ul>
          </section>

          <section className="privacy-section">
            <h2>How We Use Your Information</h2>
            <p>Your information is used for the following purposes:</p>
            <ul>
              <li>To provide counselling and psychological services</li>
              <li>To maintain accurate client records as required by HPCSA</li>
              <li>To process medical aid claims and billing</li>
              <li>
                To communicate with you regarding appointments and
                session-related matters
              </li>
              <li>To comply with legal and professional obligations</li>
              <li>To conduct psychometric assessments and provide feedback</li>
            </ul>
          </section>

          <section className="privacy-section">
            <h2>Confidentiality</h2>
            <p>
              As a registered counsellor with the HPCSA (Registration Number:
              PRC0045160), I am bound by strict confidentiality requirements.
              All information shared during counselling sessions is kept
              confidential, except in the following circumstances:
            </p>
            <ul>
              <li>
                Where you have provided written consent to disclose information
              </li>
              <li>Where there is a risk of harm to yourself or others</li>
              <li>Where disclosure is required by law or court order</li>
              <li>
                Where there is suspected abuse of a child or vulnerable person
              </li>
              <li>
                For supervision and professional consultation purposes (with
                identifying information protected)
              </li>
            </ul>
          </section>

          <section className="privacy-section">
            <h2>Storage and Security of Information</h2>
            <p>
              Your personal and health information is stored securely in
              compliance with POPIA requirements:
            </p>
            <ul>
              <li>
                Physical records are stored in locked, secure filing systems
              </li>
              <li>Electronic records are password-protected and encrypted</li>
              <li>
                Access to your information is restricted to authorized personnel
                only
              </li>
              <li>
                Records are retained for a minimum of 7 years as required by
                HPCSA regulations
              </li>
              <li>
                When records are no longer required, they are destroyed securely
                and confidentially
              </li>
            </ul>
          </section>

          <section className="privacy-section">
            <h2>Your Rights Under POPIA</h2>
            <p>You have the right to:</p>
            <ul>
              <li>Access your personal information held by us</li>
              <li>Request correction of inaccurate information</li>
              <li>Object to the processing of your information</li>
              <li>
                Request deletion of your information (subject to legal retention
                requirements)
              </li>
              <li>
                Lodge a complaint with the Information Regulator if you believe
                your privacy rights have been violated
              </li>
            </ul>
          </section>

          <section className="privacy-section">
            <h2>Medical Aid and Third Parties</h2>
            <p>
              If you choose to claim from your medical aid, certain information
              will be shared with your medical scheme for billing and
              authorization purposes. This information is limited to what is
              necessary for processing claims and is shared securely. You will
              be informed before any information is shared with third parties.
            </p>
          </section>

          <section className="privacy-section">
            <h2>Website and Online Communication</h2>
            <p>
              When you use our website or communicate with us electronically:
            </p>
            <ul>
              <li>
                We may collect technical information such as IP addresses and
                browser data for website functionality
              </li>
              <li>
                We use secure communication channels for sensitive information
              </li>
              <li>
                We do not sell or share your information with third parties for
                marketing purposes
              </li>
              <li>Cookies may be used to enhance your browsing experience</li>
            </ul>
          </section>

          <section className="privacy-section">
            <h2>Virtual Counselling Sessions</h2>
            <p>
              Virtual counselling sessions are conducted via secure platforms
              (Google Meet). While we take measures to ensure privacy and
              security, please be aware that online communication carries
              inherent risks. You are responsible for ensuring your environment
              is private during virtual sessions.
            </p>
          </section>

          <section className="privacy-section">
            <h2>Changes to This Privacy Policy</h2>
            <p>
              We may update this Privacy Policy from time to time to reflect
              changes in our practices or legal requirements. Any changes will
              be posted on this page with an updated revision date.
            </p>
          </section>

          <section className="privacy-section">
            <h2>Contact Information</h2>
            <p>
              If you have any questions about this Privacy Policy or wish to
              exercise your rights under POPIA, please contact:
            </p>
            <div className="contact-details">
              <p>
                <strong>Erin Robinson Counselling</strong>
              </p>
              <p>Email: erinrobinsoncounselling@gmail.com</p>
              <p>Phone/WhatsApp: +27 71 496 7512</p>
              <p>HPCSA Registration: PRC0045160</p>
              <p>Practice Number: 1266004</p>
            </div>
          </section>

          <section className="privacy-section">
            <h2>Information Regulator Contact Details</h2>
            <p>
              If you are not satisfied with how your personal information is
              handled, you may lodge a complaint with:
            </p>
            <div className="contact-details">
              <p>
                <strong>The Information Regulator (South Africa)</strong>
              </p>
              <p>
                Website:{" "}
                <a
                  href="https://www.justice.gov.za/inforeg/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  www.justice.gov.za/inforeg
                </a>
              </p>
              <p>Email: inforeg@justice.gov.za</p>
            </div>
          </section>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default PrivacyPage;
